import { Observable, map, of } from 'rxjs';

import { GridRow } from '../services/dto/grid-row';
import { InsertPosition } from '../services/dto/insert-position';
import { RowType } from '../services/dto/row-type';
import { GridMutationResult, GridMutationUpdate, IEditabledDataService, IEditabledDataUpdateFields } from './types';

export interface CamGridLocalRowsOptions<T extends GridRow> {
  createRow: (id: string, type: RowType) => T;
  /** Champs calcules, appliques a la creation puis apres chaque edition (`previous` : la ligne avant l'edition). */
  derive?: (row: T, previous?: T) => Partial<T>;
  /** Patches asynchrones (ex. prix) appliques apres chaque mutation ; seules les valeurs changees remontent. */
  enrich$?: (rows: T[]) => Observable<IEditabledDataUpdateFields<T>[]>;
  onChange?: (rows: T[]) => void;
}

/** Liste plate editee en memoire, qui joue le role du back pour `cam-ag-grid-editabled`. */
export class CamGridLocalRows<T extends GridRow> {
  private _options: CamGridLocalRowsOptions<T>;
  private _rows: T[];

  constructor(rows: T[], options: CamGridLocalRowsOptions<T>) {
    this._options = options;
    this._rows = rows.map(row => this._derive({ ...row }));
    this._renumber();
  }

  public snapshot(): T[] {
    return this._rows.map(row => ({ ...row }));
  }

  public dataService(): IEditabledDataService<T> {
    const commit$ = (result: GridMutationResult<T>) => {
      const { enrich$, onChange } = this._options;
      if (!enrich$ && !onChange) {
        return of(result);
      }
      const rows = this.snapshot();
      onChange?.(rows);
      return this._enrich$(result, rows);
    };

    return {
      addLine$: (type, relatedId, position) => commit$(this._add(type, relatedId, position)),
      deleteLine$: id => commit$(this._remove(id)),
      duplicateLineTo$: (sourceId, targetId, position) => commit$(this._duplicate(sourceId, targetId, position)),
      getData$: () =>
        this._options.enrich$
          ? this._enrich$({}, this.snapshot()).pipe(map(() => ({ items: this.snapshot() })))
          : of({ items: this.snapshot() }),
      moveLine$: (id, targetId, position) => commit$(this._move(id, targetId, position)),
      updateField$: fields => commit$(this._update(fields)),
    };
  }

  private _add(type: RowType, relatedId?: string, position?: InsertPosition): GridMutationResult<T> {
    const row = this._derive(this._flat(this._options.createRow(crypto.randomUUID(), type)));
    this._insert(row, relatedId, position);
    const update = this._renumber(row.id);
    return { add: [{ ...row }], update };
  }

  private _duplicate(sourceId: string, targetId: string, position: InsertPosition): GridMutationResult<T> {
    const source = this._rows.find(row => row.id === sourceId);
    if (!source) {
      return {};
    }

    const row = this._flat({ ...source, id: crypto.randomUUID() });
    this._insert(row, targetId, position);
    const update = this._renumber(row.id);
    return { add: [{ ...row }], update };
  }

  private _move(id: string, targetId: string, position: InsertPosition): GridMutationResult<T> {
    const row = this._rows.find(candidate => candidate.id === id);
    if (!row || id === targetId) {
      return {};
    }

    this._rows = this._rows.filter(candidate => candidate.id !== id);
    this._insert(row, targetId, position);
    return { update: this._renumber() };
  }

  private _remove(id: string): GridMutationResult<T> {
    this._rows = this._rows.filter(row => row.id !== id);
    return { remove: [{ id }], update: this._renumber() };
  }

  private _update(fields: IEditabledDataUpdateFields<T>[]): GridMutationResult<T> {
    return {
      update: fields.flatMap(({ id, ...changes }) => {
        const row = this._rows.find(candidate => candidate.id === id);
        const keys = Object.keys(changes) as (keyof T & string)[];
        if (!row || keys.every(key => row[key] === (changes as Partial<T>)[key])) {
          return [];
        }

        const before = { ...row };
        this._derive(Object.assign(row, changes), before);

        const modifiedProperties = (Object.keys(row) as (keyof T & string)[]).filter(key => row[key] !== before[key]);
        return [{ modifiedProperties, row: { ...row } }];
      }),
    };
  }

  private _enrich$(result: GridMutationResult<T>, rows: T[]): Observable<GridMutationResult<T>> {
    if (!this._options.enrich$) {
      return of(result);
    }

    return this._options.enrich$(rows).pipe(
      map(patches => {
        const updates = new Map((result.update ?? []).map(update => [update.row.id, update]));
        (this._update(patches).update ?? []).forEach(({ modifiedProperties = [], row }) => {
          const pending = updates.get(row.id)?.modifiedProperties ?? [];
          updates.set(row.id, { modifiedProperties: [...new Set([...pending, ...modifiedProperties])], row });
        });
        return { ...result, update: [...updates.values()] };
      })
    );
  }

  private _derive(row: T, previous?: T): T {
    return Object.assign(row, this._options.derive?.(row, previous));
  }

  private _flat(row: T): T {
    return { ...row, compositionPath: [row.id], hasChildren: false, parentId: null };
  }

  // liste plate : `Inside` est traite comme `Below`
  private _insert(row: T, relatedId?: string, position?: InsertPosition) {
    const target = relatedId ? this._rows.findIndex(candidate => candidate.id === relatedId) : -1;
    if (target < 0) {
      this._rows.push(row);
      return;
    }

    this._rows.splice(position === InsertPosition.Above ? target : target + 1, 0, row);
  }

  // `order` change => le datasource re-trie le niveau, c'est ce qui rend le deplacement visible
  private _renumber(excludeId?: string): GridMutationUpdate<T>[] {
    return this._rows.flatMap((row, index) => {
      const order = String(index + 1);
      if (row.index === index && row.order === order) {
        return [];
      }

      row.index = index;
      row.order = order;
      return row.id === excludeId ? [] : [{ modifiedProperties: ['index', 'order'], row: { ...row } }];
    });
  }
}
